import { Button, LinkStyled } from '@components/common';
import { useTranslation } from 'next-i18next';
import { useJobTalentActions } from '@hooks/features/useJobTalentActions';
import { CheckIcon, PhoneIcon } from '@heroicons/react/24/solid';
import { ArchiveBoxIcon, InformationCircleIcon } from '@heroicons/react/24/outline';
import { JobListType, JobPlacement } from '@type/job-params';
import { JobQuestion } from '@type/v1-api-types';

interface CardJobTalentActionsProps {
  job: ApiSchema<'JobItem'>,
  requestId?: string,
  position?: number,
  listType?: JobListType,
}

const interviewsPhoneNumber = process.env.NEXT_PUBLIC_INTERVIEWS_PHONE_NUMBER;

export const CardJobTalentActions = (props: CardJobTalentActionsProps) => {
  // Destructure props
  const {
    job,
    requestId,
    position,
    listType,
  } = props;

  // Translation
  const { t } = useTranslation('common');

  // Job Talent Actions Hook
  // *****************************************
  const {
    isApplying,
    applyToJob,
    hasApplied,
    callForInterview,
  } = useJobTalentActions({
    jobSlug: job.slug ?? '',
    jobId: job.id,
    jobTitle: job.title,
    company: job.companyName,
    likedProp: job.liked,
    dislikedProp: job.disliked,
    appliedProp: !!job.appliedAt,
    requestId,
    position,
    placement: JobPlacement.CARD,
    listType,
    withEasyApply: true,
    hasOwnApplyUrl: job.hasOwnApplyUrl,
    jobQuestions: job?.unansweredQuestions?.map(({ id, text }) => ({
      id: id?.toString(),
      question: text,
    })) as JobQuestion[],
    allowAfterApply: !job?.workplaceJob,
  });

  const showPhoneInterviewButton = interviewsPhoneNumber && false; // Currently disabled due to quota limit on agent, will be enabled later

  // Render component for job card actions
  // *****************************************
  const renderJobAction = () => {
    if (hasApplied) {
      return (
        <>
          {/* Already applied */}
          <LinkStyled
            href="#"
            color="primary-light"
            rounding="full"
            styling="soft"
            size="xs"
            onClick={callForInterview}
            className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          >
            <InformationCircleIcon className="-ml-1 mr-2 size-5 shrink-0" />
            <span className="text-center">{t('job.applied')}</span>
          </LinkStyled>
        </>
      );
    }
    if (job?.state === 'archived') {
      return (
        <>
          {/* Job archived message */}
          <LinkStyled
            href="#"
            color="primary-light"
            rounding="full"
            styling="soft"
            size="xs"
            onClick={callForInterview}
            className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          >
            <ArchiveBoxIcon className="-ml-1 mr-2 size-5 shrink-0" />
            <span className="text-center">{t('global.bnt.archived.job')}</span>
          </LinkStyled>
        </>
      );
    }

    return (
      <>
        {/* Phone interview button */}
        {showPhoneInterviewButton && (
          <LinkStyled
            href={`tel:${interviewsPhoneNumber}`}
            color="primary-light"
            rounding="full"
            styling="soft"
            size="xs"
            onClick={callForInterview}
            className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          >
            <PhoneIcon className="-ml-1 mr-2 size-5 shrink-0" />
            <span className="text-center">{t('call.for.interview.hint')}</span>
          </LinkStyled>
        )}

        {/* Fast apply button */}
        <Button
          styling="soft"
          color="secondary-light"
          rounding="full"
          size="xs"
          className="flex flex-1 items-center justify-center overflow-hidden !text-xs"
          isLoading={isApplying}
          spinnerClass="w-5 h-5 -ml-1 mr-2"
          onClick={applyToJob}
        >
          {!isApplying && <CheckIcon className="-ml-1 mr-2 size-5 shrink-0" />}
          <span className="text-center">
            {(() => {
              if (job?.state === 'active') {
                return t('job.tooltip.apply');
              }
              if (job?.state === 'passive') {
                return t('global.bnt.fast.apply.workplace.job');
              }
              return null;
            })()}
          </span>
        </Button>
      </>
    );
  };

  return (
    <div className="mb-3 flex items-stretch justify-center space-x-1.5">
      {renderJobAction()}
    </div>
  );
};
